import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import ContactCard from 'features/co-pilot/campaign-overview/contact-card';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ColumnId, getColumnAccent } from './pipeline-utils';

interface Props {
  id: string;
  title: string;
  contacts: FunnelContact[];
  funnelId: string | null;
  handleOpenContactDrawer: (linkedinUrl: string | undefined, profileId: string | undefined, funnelId: string | undefined) => () => void;
  flashId?: string | null;
}

const PipelineColumn: React.FC<Props> = ({ id, title, contacts, funnelId, handleOpenContactDrawer, flashId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const translatedTitle = t(title);
  const accent = getColumnAccent(id as ColumnId);

  return (
    <Stack className={classes.column} gap={'10px'}>
      <Stack gap={'8px'}>
        <Box className={classes.accent} sx={{ backgroundColor: accent }} />
        <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
          <Typography className={classes.header}>{translatedTitle}</Typography>
          <Typography className={classes.count}>{contacts.length}</Typography>
        </Stack>
      </Stack>
      <Droppable droppableId={id} ignoreContainerClipping={true}>
        {(provided, dropSnapshot) => (
          <Stack
            ref={provided.innerRef}
            {...provided.droppableProps}
            gap={'8px'}
            className={classes.droppableArea}
            sx={{
              backgroundColor: dropSnapshot.isDraggingOver ? theme.palette.backgrounds.main : 'transparent',
              border: dropSnapshot.isDraggingOver ? `2px dashed ${theme.palette.primary.main}` : '2px dashed transparent',
              transition: 'background-color .2s, border .2s',
            }}
          >
            {contacts.map((contact, index) => {
              const baseId = contact.profileIds?.linkedin || contact.linkedInURL || `unknown-${index}`;
              const funnelSource = (contact as any).originFunnelId ?? funnelId ?? 'solo';
              const dragId = `${baseId}-${funnelSource}`;
              return (
                <Draggable draggableId={dragId} index={index} key={dragId}>
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={{
                        ...((dragProvided.draggableProps.style as React.CSSProperties) || {}),
                        borderRadius: '10px',
                        ...(dragSnapshot.isDragging && {
                          boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
                          transition: 'box-shadow .15s ease',
                        }),
                        ...(flashId && flashId === (contact.profileIds?.linkedin || contact.linkedInURL)
                          ? { backgroundColor: theme.palette.success.light, transition: 'background-color .4s' }
                          : {}),
                      }}
                    >
                      <ContactCard
                        contact={contact}
                        handleOpenContactDetailsDialog={() => () => {
                          handleOpenContactDrawer(
                            contact.linkedInURL,
                            contact.profileIds?.linkedin,
                            (contact as any).originFunnelId ?? funnelId ?? undefined
                          )();
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Stack>
        )}
      </Droppable>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  column: {
    width: 280,
    minWidth: 280,
  },
  accent: {
    height: '3px',
    borderRadius: '2px',
  },
  header: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
  count: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    background: '#E8E8F2',
    borderRadius: '10px',
    padding: '2px 8px',
    lineHeight: 1.4,
  },
  droppableArea: {
    minHeight: '80px',
    borderRadius: theme.spacing(1),
  },
}));

export default PipelineColumn;
